import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { X, Send, ArrowLeft, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function ChatPanel({ group, currentUserEmail, isAdmin, onClose }) {
  const [inbox, setInbox] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isAdmin) {
      fetchInbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, isAdmin]);

  const fetchInbox = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat-inbox`, { credentials: "include" });
      if (res.ok) setInbox(await res.json());
    } catch (err) {
      console.error("Failed to fetch inbox:", err);
    }
  };

  const fetchMessages = async (memberId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${memberId}`, { credentials: "include" });
      if (res.ok) setMessages(await res.json());
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    fetchMessages(member.member_id);
  };

  const handleSend = async () => {
    if (!text.trim() || !selectedMember) return;
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/chat/${selectedMember.member_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: text.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setText("");
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } catch {
      toast.error("Failed to send");
    }
  };

  // Non-admin: auto-select themselves
  useEffect(() => {
    if (!isAdmin && group) {
      // Find current user's member record
      const findSelf = async () => {
        try {
          const res = await fetch(`${API}/groups/${group.group_id}/members`, { credentials: "include" });
          if (res.ok) {
            const members = await res.json();
            const self = members.find(m => m.email === currentUserEmail);
            if (self) {
              setSelectedMember(self);
              fetchMessages(self.member_id);
            }
          }
        } catch (err) {
          console.error(err);
        }
      };
      findSelf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, isAdmin, currentUserEmail]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="chat-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-[#1e2126] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          {selectedMember && isAdmin && (
            <button onClick={() => setSelectedMember(null)} className="p-1 hover:bg-white/10 rounded">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">
              {selectedMember ? `Chat with ${selectedMember.name}` : `${group.name} - Messages`}
            </h2>
            {!selectedMember && isAdmin && (
              <p className="text-xs text-gray-500">Select a member to view chat</p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="chat-close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Inbox (admin only, when no member selected) */}
        {isAdmin && !selectedMember && (
          <ScrollArea className="flex-1">
            <div className="p-3">
              {inbox.length === 0 && (
                <div className="text-center py-10">
                  <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No messages yet</p>
                </div>
              )}
              {inbox.map(({ member, last_message, message_count }) => (
                <button
                  key={member.member_id}
                  data-testid={`inbox-${member.member_id}`}
                  onClick={() => handleSelectMember(member)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
                    style={{ backgroundColor: member.color }}
                  >
                    {member.initials}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-white">{member.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {last_message ? last_message.text : "No messages"}
                    </p>
                  </div>
                  {message_count > 0 && (
                    <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full">{message_count}</span>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Chat Messages */}
        {selectedMember && (
          <>
            <ScrollArea className="flex-1 p-4">
              {loading && <p className="text-center text-gray-500 text-sm py-4">Loading...</p>}
              {!loading && messages.length === 0 && (
                <div className="text-center py-10">
                  <MessageCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No messages yet. Start a conversation!</p>
                </div>
              )}
              {messages.map((msg) => {
                const isSelf = msg.sender_email === currentUserEmail;
                return (
                  <div key={msg.msg_id} className={`flex mb-3 ${isSelf ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isSelf ? 'order-2' : ''}`}>
                      {!isSelf && (
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-medium text-white"
                            style={{ backgroundColor: msg.sender_color || "#6b7280" }}
                          >
                            {msg.sender_initials}
                          </div>
                          <span className="text-xs text-gray-500">{msg.sender_name}</span>
                        </div>
                      )}
                      <div className={`px-3 py-2 rounded-xl text-sm ${
                        isSelf 
                          ? 'bg-blue-500 text-white rounded-br-sm' 
                          : 'bg-white/10 text-white rounded-bl-sm'
                      }`}>
                        {msg.text}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-0.5 px-1">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  data-testid="chat-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
                />
                <button
                  data-testid="chat-send"
                  onClick={handleSend}
                  className="p-2.5 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
